import dayjs from "dayjs";
import { components } from "../../types/api";
import { canViewGoalField } from "../briefingPermissions";
import { getCountryLabel } from "../countryUtils";
import { resolveTemplate } from "./templates";
import {
  ClaudeTemplate,
  CompanyAccountField,
  PromptValues,
  ResolvedTemplate,
} from "./types";
import { createNumberFormat } from "../numberFormat";

type Role = components["schemas"]["Role"];
type AssociatedCampaign = components["schemas"]["AssociatedCampaignResponse"];
type Briefing = {
  name?: string | null;
  campaignName?: string | null;
  brandOrProduct?: string | null;
  companyName?: string | null;
};

/** Qué significa cada placeholder. Sirve de etiqueta en los avisos de dato ausente. */
export const PLACEHOLDER_LABELS: Record<string, string> = {
  tool: "Herramienta",
  url: "URL de la herramienta",
  hierarchy: "Jerarquía",
  idToReturn: "ID a devolver",
  idHint: "Dónde ver el ID",
  client: "Cliente",
  accountIds: "ID de cuenta / advertiser",
  "briefing.name": "Nombre del briefing",
  "briefing.campaign": "Campaña del briefing",
  brand: "Marca o producto",
  subcampaign: "Subcampaña",
  platform: "Plataforma",
  campaignType: "Tipo de campaña",
  format: "Formato",
  level: "Nivel a vincular",
  investmentPayment: "Quién paga la inversión",
  nativeProvider: "Proveedor nativo",
  currentSourceId: "Source ID ya vinculado",
  "names.line": "Nombre de línea",
  "names.order": "Nombre de insertion order / grupo",
  "names.campaign": "Nombre de campaña",
  "names.concept": "Concepto de campaña",
  "names.beapolo": "Nombre de la línea en BeApolo",
  "goal.startDate": "Fecha de inicio",
  "goal.endDate": "Fecha de fin",
  "goal.country": "País",
  "goal.objective": "Objetivo de compra",
  "goal.kpi": "Objetivo de compra",
  "goal.funnel": "Funnel",
  "goal.currency": "Moneda",
  "goal.volume": "Volumen previsto",
  "goal.budget": "Presupuesto en plataforma",
  "goal.netUnitCost": "Coste neto unitario",
  "goal.ctr": "CTR previsto",
  "goal.visibility": "Visibilidad mínima",
  "goal.notes": "Notas del goal",
  "target.gender": "Sexo",
  "target.age": "Edad",
  "target.language": "Idioma",
  "target.geography": "Geografía",
  "target.technology": "Tecnología / dispositivos",
  "target.interests": "Intereses",
  "target.audience": "Audiencias de datos",
  "target.exclusions": "Exclusiones",
  "target.notes": "Notas de segmentación",
  "assets.creatives": "Creatividades",
  "assets.copies": "Copies",
  "assets.extra": "Otros enlaces",
  "assets.destinations": "URLs de destino",
};

/**
 * Datos que pueden no venir sin que haga falta parar: se marcan como "no indicado"
 * en vez de con el aviso de dato ausente.
 */
const OPTIONAL_KEYS = new Set([
  "currentSourceId",
  "nativeProvider",
  "investmentPayment",
  "goal.ctr",
  "goal.visibility",
  "goal.notes",
  "target.language",
  "target.technology",
  "target.interests",
  "target.audience",
  "target.exclusions",
  "target.notes",
  "assets.copies",
  "assets.extra",
  "names.concept",
]);

const PLACEHOLDER_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

const missing = (key: string): string =>
  OPTIONAL_KEYS.has(key)
    ? "— (no indicado)"
    : `⚠️ FALTA EN EL BRIEFING (${PLACEHOLDER_LABELS[key] ?? key})`;

/** Sustituye los `{{clave}}` de un texto por sus valores o por el aviso de dato ausente. */
export const fillPlaceholders = (text: string, values: PromptValues): string =>
  text.replace(PLACEHOLDER_RE, (_match, key: string) => {
    const value = values[key];
    return value === null || value === undefined || value === ""
      ? missing(key)
      : value;
  });

/** Claves usadas en un texto, para validar plantillas. */
export const extractPlaceholders = (text: string): string[] =>
  Array.from(text.matchAll(PLACEHOLDER_RE), (m) => m[1]);

const text = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed && trimmed !== "UNDEFINED" ? trimmed : null;
};

/** Un 0 en goal es el valor por defecto de la base de datos, no un dato. */
const positive = (value?: number | null): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;

const formatNumber = (value: number, maxDecimals = 2): string =>
  createNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(value);

const OBJECTIVE_HINTS: Record<string, string> = {
  CPM: "CPM — compra por mil impresiones (reconocimiento / alcance)",
  CPC: "CPC — compra por clic (tráfico)",
  CPMV: "CPMV — compra por mil impresiones visibles",
  CPV: "CPV — compra por visualización de vídeo",
  CPE: "CPE — compra por interacción (engagement)",
  CPA: "CPA — compra por acción / conversión",
  CPL: "CPL — compra por lead",
};

const VOLUME_UNITS: Record<string, string> = {
  CPM: "impresiones",
  CPMV: "impresiones visibles",
  CPC: "clics",
  CPV: "visualizaciones",
  CPE: "interacciones",
  CPA: "conversiones",
  CPL: "leads",
};

const GENDER_LABELS: Record<string, string> = {
  MAN: "Hombres",
  WOMAN: "Mujeres",
  BOTH: "Hombres y mujeres",
};

const LEVEL_LABELS: Record<string, string> = {
  CAMPAIGN: "Campaña",
  INSERTIONORDER: "Insertion order",
  LINEITEM: "Line item",
};

const ID_TO_RETURN: Record<string, string> = {
  CAMPAIGN: "ID de la campaña",
  INSERTIONORDER: "ID del insertion order",
  LINEITEM: "ID del line item",
};

/** Los enlaces de assets llegan como objeto o como JSON en texto (registros antiguos). */
const parseLinks = (value: unknown): Record<string, string> => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return parseLinks(JSON.parse(value));
    } catch {
      return value.trim() ? { link: value.trim() } : {};
    }
  }
  if (typeof value !== "object") return {};
  return Object.entries(value as Record<string, unknown>).reduce(
    (acc, [key, link]) => {
      const str = typeof link === "string" ? link.trim() : String(link ?? "");
      if (str) acc[key] = str;
      return acc;
    },
    {} as Record<string, string>
  );
};

const bulletList = (items: string[]): string | null =>
  items.length ? items.map((item) => `\n    - ${item}`).join("") : null;

const linkList = (value: unknown): string | null =>
  bulletList(
    Object.entries(parseLinks(value)).map(([label, link]) =>
      label === link || label === "link" ? link : `${label}: ${link}`
    )
  );

export interface BuildClaudePromptInput {
  associatedCampaign: AssociatedCampaign;
  briefing?: Briefing | null;
  role?: Role | null;
  /** IDs de cuenta de la empresa por herramienta, si se han podido leer. */
  companyAccounts?: Partial<
    Record<CompanyAccountField, string[] | null>
  > | null;
}

export interface ClaudePrompt {
  prompt: string;
  resolved: ResolvedTemplate;
  /** Bloques del briefing que están vacíos (goal, target, asset). */
  missingSections: ("goal" | "target" | "asset")[];
}

export const buildPromptValues = (
  input: BuildClaudePromptInput,
  template: ClaudeTemplate
): PromptValues => {
  const { associatedCampaign: ac, briefing, role, companyAccounts } = input;
  const goal = ac.goal;
  const target = ac.target;
  const asset = ac.asset;

  // Lo que el rol no puede ver en pantalla tampoco va al prompt.
  const goalField = (field: string, value: string | null): string | null =>
    role && !canViewGoalField(role, field) ? "(no visible para tu rol)" : value;

  const currency = text(goal?.currency);
  const money = (value?: number | null, decimals = 2): string | null => {
    const n = positive(value);
    return n === null
      ? null
      : `${formatNumber(n, decimals)}${currency ? ` ${currency}` : ""}`;
  };

  const objective = text(goal?.objective);
  const netUnitCost = money(goal?.netUnitCost, 4);
  const volume = positive(goal?.expectedVolume);
  const ctr = positive(goal?.ctr ?? target?.ctr);
  const visibility = positive(goal?.visibility);
  const country = text(goal?.country);

  const minAge = target?.minimumAge;
  const maxAge = target?.maximumAge;

  const accountIds = [
    ...((template.accountField && companyAccounts?.[template.accountField]) ||
      []),
    ...(template.accountField === "dv360AdvertiserIds" && ac.advertiserId
      ? [ac.advertiserId]
      : []),
  ];

  const destinations = (asset?.destinations ?? [])
    .filter((d) => text(d.url))
    .map((d) => {
      const label = text(d.label);
      const utm = text(d.utm);
      return `${label ? `${label}: ` : ""}${d.url}${utm ? ` (UTM: ${utm})` : ""}`;
    });
  if (!destinations.length && text(asset?.destinationUrl)) {
    const utm = text(asset?.utm);
    destinations.push(`${asset?.destinationUrl}${utm ? ` (UTM: ${utm})` : ""}`);
  }

  return {
    tool: template.tool,
    url: template.url,
    hierarchy: template.hierarchy,
    client: text(briefing?.companyName),
    accountIds: accountIds.length
      ? Array.from(new Set(accountIds)).join(", ")
      : null,
    "briefing.name": text(briefing?.name),
    "briefing.campaign": text(briefing?.campaignName),
    brand: text(briefing?.brandOrProduct),
    subcampaign: text(ac.subcampaignName),
    platform: text(ac.platform),
    campaignType: text(ac.briefingCampaignType),
    format: text(ac.campaignFormat),
    level: LEVEL_LABELS[ac.campaignLevel] ?? text(ac.campaignLevel),
    investmentPayment:
      ac.investmentPayment === "CLIENTE"
        ? "El cliente (la inversión va en su cuenta)"
        : ac.investmentPayment === "TI_MEDIA_GROUP_SL"
          ? "TI Media Group SL"
          : null,
    nativeProvider: text(ac.nativeAdProvider),
    currentSourceId: text(ac.sourceId),
    "names.campaign": goalField("campaign", text(goal?.campaign)),
    "names.order": goalField("orderName", text(goal?.orderName)),
    "names.line": goalField("lineName", text(goal?.lineName)),
    "names.concept": goalField("campaignConcept", text(goal?.campaignConcept)),
    "names.beapolo": text(ac.name),
    "goal.startDate": goalField(
      "startDate",
      goal?.startDate ? dayjs(goal.startDate).format("YYYY-MM-DD") : null
    ),
    "goal.endDate": goalField(
      "endDate",
      goal?.endDate ? dayjs(goal.endDate).format("YYYY-MM-DD") : null
    ),
    "goal.country": goalField(
      "country",
      country ? `${getCountryLabel(country)} (${country})` : null
    ),
    "goal.objective": goalField(
      "objective",
      objective ? (OBJECTIVE_HINTS[objective] ?? objective) : null
    ),
    "goal.kpi": goalField("objective", objective),
    "goal.funnel": goalField("funnel", text(goal?.funnel)),
    "goal.currency": goalField("currency", currency),
    "goal.volume": goalField(
      "expectedVolume",
      volume === null
        ? null
        : `${formatNumber(volume, 0)}${objective && VOLUME_UNITS[objective] ? ` ${VOLUME_UNITS[objective]}` : ""}`
    ),
    "goal.budget": goalField(
      "platformInversion",
      money(goal?.platformInversion)
    ),
    "goal.netUnitCost": goalField("netUnitCost", netUnitCost),
    "goal.ctr": goalField(
      "ctr",
      ctr === null ? null : `${formatNumber(ctr)} %`
    ),
    "goal.visibility": goalField(
      "visibility",
      visibility === null ? null : `${formatNumber(visibility)} %`
    ),
    "goal.notes": goalField("notes", text(goal?.notes)),
    "target.gender": target ? (GENDER_LABELS[target.sex] ?? target.sex) : null,
    "target.age":
      typeof minAge === "number" && typeof maxAge === "number"
        ? `${minAge}–${maxAge} años`
        : null,
    "target.language": text(target?.language),
    "target.geography":
      text(target?.geography) ??
      (country ? `Todo ${getCountryLabel(country)}` : null),
    "target.technology": text(target?.technology),
    "target.interests": text(target?.interests),
    "target.audience": text(target?.dataAudience),
    "target.exclusions": text(target?.exclusions),
    "target.notes": text(target?.notes),
    "assets.creatives": linkList(asset?.assetsLinks),
    "assets.copies": linkList(asset?.copiesLinks),
    "assets.extra": linkList(asset?.additionalLinks),
    "assets.destinations": bulletList(destinations),
  };
};

const PREAMBLE = `# Crear subcampaña en {{tool}}: {{names.line}}

> ⚙️ **Prompt generado automáticamente por BeApolo (beta)** a partir del briefing, con una plantilla genérica para {{tool}}.
> Puede que algún paso no esté bien especificado, que la interfaz de {{tool}} haya cambiado o que falten datos. Tómalo como guía, no como verdad absoluta: usa tu criterio y pregúntame cuando dudes.

## Contexto
Eres el técnico de campañas de la agencia. Tienes que montar en **{{tool}}** ({{url}}) una subcampaña de un briefing ya aprobado. Trabajas en mi navegador, con mi sesión ya iniciada.

## Reglas (léelas antes de empezar)
1. **Puede que algún paso ya esté hecho** (la herramienta abierta, la cuenta seleccionada, la campaña padre creada…). Antes de cada paso mira en qué pantalla estás y salta lo que ya esté hecho. No dupliques nada: si ya existe una entidad con el mismo nombre, para y pregúntame si la reutilizo.
2. **No inventes datos.** Lo marcado con "⚠️ FALTA EN EL BRIEFING" no está en el briefing. Si es imprescindible (presupuesto, fechas, cuenta, creatividades), para y pregúntame. Si no lo es, deja el valor por defecto de la plataforma y apúntalo para el resumen final. Lo marcado como "no indicado" es opcional.
3. **Si algo no encaja** con lo que ves (una opción que no existe, un nombre distinto, un formato no permitido), elige la opción más parecida, dímelo y sigue. Si la duda afecta al dinero (presupuesto, puja, moneda) o a la segmentación, pregunta antes.
4. **No actives nada.** Deja todo en pausa o en borrador. No publiques, no lances, no aceptes cargos ni condiciones sin mi confirmación explícita.
5. **No toques lo que ya existía**: no modifiques ni borres campañas, grupos, líneas ni anuncios previos.
6. **Copia los nombres tal cual** (mayúsculas, guiones bajos, sin espacios añadidos).
7. Ve paso a paso y cuéntame en una línea qué has hecho en cada uno. Si te bloqueas (login, 2FA, permisos, captcha, ventana emergente), para y avísame.

## Jerarquía en {{tool}}
{{hierarchy}}
`;

const DATA_BLOCK = `## Datos del briefing
### Identificación
- Cliente: {{client}}
- ID(s) de cuenta / advertiser en {{tool}}: {{accountIds}}
- Briefing: {{briefing.name}} · campaña: {{briefing.campaign}} · marca/producto: {{brand}}
- Subcampaña: {{subcampaign}}
- Plataforma en BeApolo: {{platform}} · tipo: {{campaignType}} · formato: {{format}}
- Proveedor nativo: {{nativeProvider}}
- Paga la inversión: {{investmentPayment}}
- Nivel que se vincula en BeApolo: {{level}}
- Source ID ya vinculado: {{currentSourceId}}

### Nombres (cópialos tal cual)
- Campaña: {{names.campaign}}
- Insertion order / grupo: {{names.order}}
- Línea / anuncio: {{names.line}}
- Concepto: {{names.concept}}

### Fechas, objetivo y presupuesto
- Fechas: {{goal.startDate}} → {{goal.endDate}}
- País: {{goal.country}}
- Funnel: {{goal.funnel}}
- Objetivo de compra: {{goal.objective}}
- Volumen previsto: {{goal.volume}}
- Presupuesto a cargar en la plataforma: {{goal.budget}}
- Coste neto unitario (referencia para la puja): {{goal.netUnitCost}}
- CTR previsto: {{goal.ctr}}
- Visibilidad mínima: {{goal.visibility}}
- Notas: {{goal.notes}}

### Segmentación
- Sexo: {{target.gender}}
- Edad: {{target.age}}
- Idioma: {{target.language}}
- Geografía: {{target.geography}}
- Tecnología / dispositivos: {{target.technology}}
- Intereses: {{target.interests}}
- Audiencias de datos: {{target.audience}}
- Exclusiones: {{target.exclusions}}
- Notas: {{target.notes}}

### Creatividades y destino
- Creatividades: {{assets.creatives}}
- Copies: {{assets.copies}}
- Otros enlaces: {{assets.extra}}
- URLs de destino: {{assets.destinations}}
`;

const CLOSING = `## Al terminar
1. Repasa lo creado contra el bloque "Datos del briefing": nombres, fechas, presupuesto, puja, segmentación y creatividades. Confirma que todo sigue en pausa o en borrador.
2. Dame un resumen corto con:
   - cada entidad creada (o reutilizada), con su nombre y su ID;
   - los valores que hayas supuesto o dejado por defecto;
   - los datos que faltaban;
   - lo que debería revisar antes de activar.
3. Lo más importante: el **{{idToReturn}}** en {{tool}} ({{idHint}}). Es el Source ID que pego en BeApolo para vincular la línea.
`;

const buildWarnings = (
  ac: AssociatedCampaign,
  missingSections: ClaudePrompt["missingSections"],
  notes: string[]
): string => {
  const sectionLabels = {
    goal: "objetivo y presupuesto (goal)",
    target: "segmentación (target)",
    asset: "creatividades (assets)",
  };
  const items = [
    ...notes,
    ...(missingSections.length
      ? [
          `El briefing todavía no tiene ${missingSections.map((s) => sectionLabels[s]).join(", ")}. Puedes avanzar con lo que haya, pero para cuando llegues a un paso que dependa de esos datos.`,
        ]
      : []),
    ...(text(ac.sourceId)
      ? [
          `Esta línea ya está vinculada en BeApolo al ID ${ac.sourceId}: es probable que la campaña ya exista. Búscala antes de crear nada.`,
        ]
      : []),
  ];
  return items.length
    ? `## Avisos para esta línea\n${items.map((i) => `- ${i}`).join("\n")}\n`
    : "";
};

export const buildClaudePrompt = (
  input: BuildClaudePromptInput
): ClaudePrompt => {
  const ac = input.associatedCampaign;
  const resolved = resolveTemplate(
    ac.platform,
    ac.briefingCampaignType,
    ac.nativeAdProvider
  );
  const { template, notes } = resolved;

  const missingSections: ClaudePrompt["missingSections"] = [];
  if (!ac.goal) missingSections.push("goal");
  if (!ac.target) missingSections.push("target");
  if (!ac.asset) missingSections.push("asset");

  const values: PromptValues = {
    ...buildPromptValues(input, template),
    idToReturn: ID_TO_RETURN[ac.campaignLevel] ?? "ID de la campaña",
    idHint: template.idHint,
  };

  const prompt = [
    PREAMBLE,
    buildWarnings(ac, missingSections, notes),
    DATA_BLOCK,
    `## Pasos\n${template.steps.trim()}\n`,
    CLOSING,
  ]
    .filter(Boolean)
    .map((part) => fillPlaceholders(part, values))
    .join("\n");

  return { prompt, resolved, missingSections };
};
